// TaskActions — the inline approve/deny control for an actionable task row.
// ✓/✗ icons animate (framer-motion spring, bounce 0) into a Confirm + "Add
// note" pair; Confirm responds in place, Add note opens the approve/deny modal
// (optional note to approve, required reason to deny). Clicking away reverts.
// A stage that needs the approver's passkey swaps ✓ for a fingerprint that
// opens the signing page straight away: it shows what is being approved, so it
// is the confirm step. Shared by the Home Tasks card and the Tasks page.

import { Box, HStack, Icon, useOutsideClick, useToast } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useRef, useState } from "react";
import { TbCheck, TbFingerprint, TbMessagePlus, TbX } from "react-icons/tb";

import ConfirmModal from "components/ds/ConfirmModal";
import UtilityButton from "components/ds/UtilityButton";
import { actionLabels, confirmModalCopy, respondToast } from "components/tasks/taskMeta";
import { MF_TASKS, type MFTaskItem } from "state/members/tasks";

import { useTaskApproval } from "./useTaskApproval";

// A lighter etched shadow for the row action buttons — the default `dropshadow`
// (elevLow) reads too heavy at this small size.
const SOFT_DROPSHADOW = {
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.9), 0 0 1px 0 rgba(0,0,0,0.16), 0 1px 2px 0 rgba(0,0,0,0.06)",
  _hover: {
    bg: "neutral.75",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.9), 0 0 1px 0 rgba(0,0,0,0.22), 0 1px 3px 0 rgba(0,0,0,0.09)",
  },
} as const;

const MotionBox = motion(Box);
// Spring swap — bounce 0 per the design-eng playbook, settles ~0.22s. Content
// slides in from the left while the outgoing buttons exit left. Interruptible.
const SWAP = { type: "spring", duration: 0.22, bounce: 0 } as const;
const swapAnim = {
  initial: { opacity: 0, x: -16 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -16 },
  transition: SWAP,
};

const TaskActions = ({ task }: { task: MFTaskItem }) => {
  const toast = useToast();
  const { respond, isLoading } = MF_TASKS.useRespondToTask(task.assigneeId);
  const approval = useTaskApproval(task, respond);
  const [mode, setMode] = useState<"idle" | "approve" | "deny">("idle");
  const [modalOpen, setModalOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref,
    handler: () => {
      if (!modalOpen) setMode("idle");
    },
  });

  const labels = actionLabels(task.entityType);
  const isApprove = mode === "approve";
  const modalCopy = confirmModalCopy(
    task.entityType,
    isApprove ? "approve" : "deny",
    task.memberName,
  );

  const finish = (decision: "approve" | "deny", note?: string) => {
    setModalOpen(false);
    setMode("idle");
    const { title, description } = respondToast(
      task.entityType,
      decision,
      task.memberName,
    );
    // An approval that went to the signing page is confirmed there.
    const answered =
      decision === "approve"
        ? approval.approve(note).then((outcome) => outcome === "approved")
        : respond(decision, note).then(() => true);
    answered
      .then((done) => {
        if (done) {
          toast({ status: decision === "approve" ? "success" : "info", title, description });
        }
      })
      // On failure the api layer already shows an error toast and the optimistic
      // removal (inside respond) is skipped, so the row stays actionable; just
      // swallow here so it isn't an unhandled rejection.
      .catch(() => undefined);
  };

  return (
    <>
      <Box ref={ref} flexShrink={0} display="flex" alignItems="center">
        <AnimatePresence mode="wait" initial={false}>
          {mode === "idle" ? (
            <MotionBox key="icons" display="flex" alignItems="center" {...swapAnim}>
              <HStack spacing="sm">
                <UtilityButton
                  size="sm"
                  treatment="dropshadow"
                  sentiment="positive"
                  iconOnly
                  aria-label={
                    approval.needsPasskey ? `${labels.approve} with passkey` : labels.approve
                  }
                  isDisabled={approval.isStarting}
                  onClick={() =>
                    approval.needsPasskey ? finish("approve") : setMode("approve")
                  }
                  {...SOFT_DROPSHADOW}
                >
                  <Icon as={approval.needsPasskey ? TbFingerprint : TbCheck} boxSize="14px" />
                </UtilityButton>
                <UtilityButton
                  size="sm"
                  treatment="dropshadow"
                  sentiment="negative"
                  iconOnly
                  aria-label={labels.deny}
                  onClick={() => setMode("deny")}
                  {...SOFT_DROPSHADOW}
                >
                  <Icon as={TbX} boxSize="14px" />
                </UtilityButton>
              </HStack>
            </MotionBox>
          ) : (
            <MotionBox key="confirm" display="flex" alignItems="center" {...swapAnim}>
              <HStack spacing="sm">
                <UtilityButton
                  size="sm"
                  treatment="subtle"
                  leftIcon={<Icon as={TbMessagePlus} boxSize="14px" />}
                  onClick={() => setModalOpen(true)}
                >
                  Add note
                </UtilityButton>
                <UtilityButton
                  size="sm"
                  treatment="dropshadow"
                  sentiment={isApprove ? "positive" : "negative"}
                  leftIcon={<Icon as={isApprove ? TbCheck : TbX} boxSize="14px" />}
                  isDisabled={isLoading}
                  onClick={() => finish(isApprove ? "approve" : "deny")}
                  {...SOFT_DROPSHADOW}
                >
                  Confirm
                </UtilityButton>
              </HStack>
            </MotionBox>
          )}
        </AnimatePresence>
      </Box>

      {modalOpen && (
        <ConfirmModal
          title={modalCopy.title}
          body={modalCopy.body}
          notePlaceholder={modalCopy.notePlaceholder}
          requireNote={!isApprove}
          confirmLabel={isApprove ? labels.approve : labels.deny}
          confirmTone={isApprove ? "success" : "danger"}
          onConfirm={(note) => finish(isApprove ? "approve" : "deny", note || undefined)}
          onCancel={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

export default TaskActions;
